// Bagi Pekerjaan Bordir — 4 langkah, satu per layar: Bagian → Operator → Jumlah → Konfirmasi. Rincian titik & tugas tetap divalidasi backend.
import { useMemo, useRef, useState } from "react";
import { Check, Plus, Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import { notify } from "./modal_center";
import { errMsg, post } from "./bordir_common";

const remainingOf = (b) => b.plan ? b.positions.reduce((a, p) => a + Math.max(0, b.pcs - p.assigned), 0) : b.pcs;
export const openBundles = (d) => d.bundles.filter(b => b.pcs > 0 && (!b.plan || (b.plan.positions.length > 0 && remainingOf(b) > 0)));

function usePositions(d, b, sel, custom) {
  return useMemo(() => {
    if (!b) return [];
    if (b.plan) return b.positions.map(p => ({ key: p.pos_id, label: p.label, points: p.points, left: Math.max(0, b.pcs - p.assigned) }));
    return [...d.positions_preset.filter(p => p.pos_id in sel).map(p => ({ key: p.pos_id, label: p.label, points: sel[p.pos_id], left: b.pcs })),
      ...custom.map((c, i) => ({ key: `c${i}`, label: c.label.trim() || "Posisi lainnya", points: c.points, left: b.pcs, custom: true }))];
  }, [d, b, sel, custom]);
}

function stepOneErrors(b, pos, custom, note) {
  if (!b || !(b.pcs > 0)) return ["Jumlah barang belum tersedia."];
  if (b.plan) return [];
  const e = [], spk = b.spk_points;
  if (!pos.length && spk !== 0) e.push("Pilih posisi bordir terlebih dahulu.");
  if (pos.some(p => !(Number(p.points) >= 1))) e.push("Isi jumlah titik untuk bagian yang dipilih.");
  if (pos.some(p => Number(p.points) > 50)) e.push("Jumlah titik maksimal 50 per posisi.");
  if (custom.some(c => !c.label.trim())) e.push("Tulis nama untuk Posisi lainnya.");
  const total = pos.reduce((a, p) => a + (Number(p.points) || 0), 0);
  if ((spk === null || spk === undefined || total !== spk) && pos.length && note.trim().length < 3)
    e.push(spk === null || spk === undefined ? "SPK belum mencatat titik — tulis dasarnya (mis. mockup / instruksi kerja)."
      : `Total ${total} titik berbeda dari SPK (${spk} titik) — tulis dasarnya (mis. mockup v2).`);
  return e;
}

// Langkah Operator hanya memeriksa siapa & bagian; jumlah pcs diperiksa di langkah Jumlah.
const PCS_ERR = (x) => x === "Isi jumlah pcs." || x.startsWith("Jumlah pcs melebihi");
const stepWhoErrors = (rows, pos, ops) => stepTwoErrors(rows, pos, ops).filter(x => !PCS_ERR(x));

function stepTwoErrors(rows, pos, ops) {
  if (!rows.length) return ["Tambahkan minimal satu operator."];
  const e = new Set(), used = {}, pairs = new Set();
  const name = (id) => ops.find(o => o.id === id)?.name || "Operator";
  for (const r of rows) {
    if (!r.op) e.add("Pilih operator penerima.");
    if (!r.pos.length) e.add("Pilih bagian bordir yang diberikan.");
    if (!(Number(r.pcs) >= 1)) e.add("Isi jumlah pcs.");
    for (const k of r.pos) {
      used[k] = (used[k] || 0) + (Number(r.pcs) || 0);
      if (r.op && pairs.has(`${r.op}|${k}`)) e.add(`${name(r.op)} sudah mendapat bagian ${pos.find(p => p.key === k)?.label} — gabungkan jumlahnya di satu baris.`);
      pairs.add(`${r.op}|${k}`);
    }
  }
  for (const p of pos) if ((used[p.key] || 0) > p.left) e.add(`Jumlah pcs melebihi sisa pekerjaan (${p.label}: sisa ${p.left} pcs).`);
  return [...e];
}

function Why({ errors, testid }) {
  if (!errors.length) return null;
  return <ul className="bw-why" role="alert" data-testid={testid}>{errors.map(x => <li key={x}>{x}</li>)}</ul>;
}

function PosPicker({ d, b, sel, setSel, custom, setCustom, note, setNote, pos, tid }) {
  const spk = b.spk_points;
  const toggle = (p) => setSel(s => {
    if (p.pos_id in s) { const n = { ...s }; delete n[p.pos_id]; return n; }
    const first = !Object.keys(s).length && !custom.length && spk > 0;
    return { ...s, [p.pos_id]: first ? spk : "" };
  });
  const total = pos.reduce((a, p) => a + (Number(p.points) || 0), 0);
  const known = spk !== null && spk !== undefined;
  return <>
    <p className="bw-q">Bagian mana yang dibordir?</p>
    <div className="bw-chips">
      {d.positions_preset.map(p => <button key={p.pos_id} type="button" className={`bw-chip ${p.pos_id in sel ? "on" : ""}`} aria-pressed={p.pos_id in sel}
        onClick={() => toggle(p)} data-testid={`bw-pos-${tid}-${p.pos_id}`}>{p.pos_id in sel && <Check size={16}/>}{p.label}</button>)}
      <button type="button" className="bw-chip" onClick={() => setCustom(c => [...c, { label: "", points: "" }])} data-testid={`bw-pos-${tid}-custom`}><Plus size={16}/> Posisi lainnya</button>
    </div>
    {pos.length > 0 && <div className="bw-list">
      {pos.map(p => <div key={p.key} className="bw-line" data-testid={`bw-pts-row-${tid}-${p.key}`}>
        {p.custom ? <input type="text" className="bw-in" placeholder="Nama posisi" value={custom[+p.key.slice(1)].label} data-testid={`bw-custom-name-${tid}-${p.key}`}
          onChange={e => setCustom(c => c.map((x, i) => i === +p.key.slice(1) ? { ...x, label: e.target.value } : x))}/> : <b>{p.label}</b>}
        <input type="number" inputMode="numeric" min={1} max={50} className="bw-in bw-num" placeholder="titik" value={p.points} aria-label={`titik ${p.label}`}
          data-testid={`bw-pts-${tid}-${p.key}`} onChange={e => {
            const v = e.target.value === "" ? "" : Math.max(0, parseInt(e.target.value, 10) || 0);
            if (p.custom) setCustom(c => c.map((x, i) => i === +p.key.slice(1) ? { ...x, points: v } : x));
            else setSel(s => ({ ...s, [p.key]: v }));
          }}/><span>titik</span>
        {p.custom && <button type="button" className="bw-icon" aria-label="Hapus posisi" onClick={() => setCustom(c => c.filter((_, i) => i !== +p.key.slice(1)))}
          data-testid={`bw-custom-del-${tid}-${p.key}`}><Trash2 size={16}/></button>}
      </div>)}
      <div className={`bw-total ${known && total === spk ? "ok" : ""}`} data-testid={`bw-total-${tid}`}>
        Total {total} titik per pcs · {known ? `SPK: ${spk} titik` : "SPK belum mencatat titik"}</div>
      {(!known || total !== spk) && <input type="text" className="bw-in bw-wide" placeholder="Dasar jumlah titik, mis. mockup v2 / instruksi kerja"
        value={note} onChange={e => setNote(e.target.value)} data-testid={`bw-note-${tid}`}/>}
    </div>}
  </>;
}

function WhoRows({ d, b, pos, rows, setRows, tid, part }) {
  const others = (k, i) => rows.reduce((a, r, j) => a + (j !== i && r.pos.includes(k) ? Number(r.pcs) || 0 : 0), 0);
  const maxFor = (r, i) => r.pos.length ? Math.min(...r.pos.map(k => (pos.find(p => p.key === k)?.left || 0) - others(k, i))) : 0;
  const set = (i, patch) => setRows(rs => rs.map((r, j) => j === i ? { ...r, ...patch } : r));
  const done = d.tasks.filter(t => t.bundle_id === b.bundle_id && t.status !== "cancelled" && !t.is_rework);
  const name = (id) => d.operators.find(o => o.id === id)?.name || "Operator";
  if (part === "pcs") return <>
    <p className="bw-q">Berapa pcs untuk masing-masing?</p>
    {rows.map((r, i) => <div key={i} className="bw-line bw-pcs-line" data-testid={`bw-row-${tid}-${i}`}>
      <b>{name(r.op)}</b><span className="bw-muted">{r.pos.map(k => pos.find(p => p.key === k)?.label).join(" + ")}</span>
      <input type="number" inputMode="numeric" min={1} className="bw-in bw-num" value={r.pcs} placeholder="pcs" aria-label={`jumlah pcs ${name(r.op)}`}
        onChange={e => set(i, { pcs: e.target.value === "" ? "" : Math.max(0, parseInt(e.target.value, 10) || 0) })} data-testid={`bw-pcs-${tid}-${i}`}/>
      <span>pcs</span><span className="bw-muted">sisa {Math.max(0, maxFor(r, i))} pcs</span>
    </div>)}
  </>;
  return <>
    <p className="bw-q">Siapa yang mengerjakan, dan bagian apa?</p>
    {done.length > 0 && <div className="bw-muted" data-testid={`bw-existing-${tid}`}>Sudah dibagi: {done.map(t => `${t.operator_name} — ${t.positions.map(p => p.label).join(", ")} ${t.pcs_assigned} pcs`).join(" · ")}</div>}
    {rows.map((r, i) => <div key={i} className="bw-card" data-testid={`bw-row-${tid}-${i}`}>
      <select className="bw-in bw-wide" value={r.op} onChange={e => set(i, { op: e.target.value })} data-testid={`bw-op-${tid}-${i}`}>
        <option value="">Pilih operator penerima…</option>
        {d.operators.map(o => <option key={o.id} value={o.id}>{o.name}{o.id === d.me_id ? " (Saya)" : ""}</option>)}
      </select>
      <div className="bw-chips">{pos.map(p => {
        const on = r.pos.includes(p.key);
        return <button key={p.key} type="button" className={`bw-chip ${on ? "on" : ""}`} aria-pressed={on} data-testid={`bw-row-pos-${tid}-${i}-${p.key}`}
          onClick={() => setRows(rs => rs.map((x, j) => {
            if (j !== i) return x;
            const np = on ? x.pos.filter(k => k !== p.key) : [...x.pos, p.key];
            const m = np.length ? Math.min(...np.map(k => (pos.find(q => q.key === k)?.left || 0) - others(k, i))) : 0;
            return { ...x, pos: np, pcs: x.pcs === "" && m > 0 ? m : x.pcs };
          }))}>{on && <Check size={16}/>}{p.label} · {p.points || "?"} titik</button>;
      })}</div>
      {rows.length > 1 && <div className="bw-line">
        {rows.length > 1 && <button type="button" className="bw-icon" aria-label="Hapus baris" onClick={() => setRows(rs => rs.filter((_, j) => j !== i))}
          data-testid={`bw-row-del-${tid}-${i}`}><Trash2 size={16}/> Hapus</button>}
      </div>}
    </div>)}
    <button type="button" className="bw-ghost" onClick={() => setRows(rs => [...rs, { op: "", pos: [], pcs: "" }])} data-testid={`bw-add-row-${tid}`}><Plus size={16}/> Tambah operator</button>
  </>;
}

function Summary({ d, b, pos, rows, tid }) {
  const name = (id) => d.operators.find(o => o.id === id)?.name || "-";
  const ops = new Set(rows.map(r => r.op));
  const left = pos.map(p => ({ ...p, rest: p.left - rows.reduce((a, r) => a + (r.pos.includes(p.key) ? Number(r.pcs) : 0), 0) })).filter(p => p.rest > 0);
  return <div data-testid={`bw-summary-${tid}`}>
    <p className="bw-big">{b.pcs} pcs dibagi kepada {ops.size} operator.</p>
    <ul className="bw-sum">{rows.map((r, i) => <li key={i} data-testid={`bw-sum-row-${tid}-${i}`}><b>{name(r.op)}:</b>{" "}
      {r.pos.map(k => { const p = pos.find(x => x.key === k); return `${p.label}, ${p.points} titik`; }).join(" + ")} — {r.pcs} pcs</li>)}</ul>
    <p className="bw-muted">Jumlah barang fisik tetap {b.pcs} pcs (tidak digandakan).</p>
    {left.length > 0 && <p className="bw-warn" data-testid={`bw-left-${tid}`}>Belum dibagi: {left.map(p => `${p.label} ${p.rest} pcs`).join(" · ")} — bisa dibagi nanti.</p>}
  </div>;
}

const STEPS = ["Bagian yang dibordir", "Operator penerima", "Jumlah pcs", "Ringkasan & konfirmasi"];

export function SplitWizard({ d, reload, onDone }) {
  const list = openBundles(d);
  const [bid, setBid] = useState(list[0]?.bundle_id || "");
  const b = list.find(x => x.bundle_id === bid) || list[0];
  const [rawStep, setStep] = useState(b?.plan ? 2 : 1);
  const [sel, setSel] = useState({});
  const [custom, setCustom] = useState([]);
  const [note, setNote] = useState("");
  const [rows, setRows] = useState([{ op: "", pos: [], pcs: "" }]);
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);
  const [tried, setTried] = useState(false);
  const pos = usePositions(d, b, sel, custom);
  if (!b) return <span className="bw-muted">Semua bagian sudah dibagi.</span>;
  const step = b.plan && rawStep === 1 ? 2 : rawStep;
  const tid = `${d.product_id}-${b.bundle_id}`;
  const e1 = stepOneErrors(b, pos, custom, note);
  const e2 = stepTwoErrors(rows, pos, d.operators);
  const eWho = stepWhoErrors(rows, pos, d.operators);
  const reset = (nb) => { setBid(nb.bundle_id); setStep(nb.plan ? 2 : 1); setSel({}); setCustom([]); setNote(""); setRows([{ op: "", pos: [], pcs: "" }]); setTried(false); };
  const next = (errs, to) => { setTried(true); if (!errs.length) { setTried(false); setStep(to); } };
  const submit = async (planOnly = false) => {
    if (lock.current) return;
    lock.current = true; setBusy(true);
    let ids = Object.fromEntries(pos.map(p => [p.key, p.key])), made = 0, ok = false;
    const opName = (id) => d.operators.find(o => o.id === id)?.name || "-";
    const brief = planOnly ? `${b.label}: tanpa titik bordir` : rows.map(r => `${opName(r.op)} — ${r.pos.map(k => pos.find(p => p.key === k)?.label).join(" + ")} ${r.pcs} pcs`).join(" · ");
    try {
      if (!b.plan) {
        const r = await post("/bordir-tasks/plan", { order_number: d.order_number, product_id: d.product_id, bundle_ids: [b.bundle_id], source_note: note,
          positions: pos.map(p => ({ pos_id: p.custom ? p.label : p.key, label: p.label, points: Number(p.points) })) });
        const saved = (r.data.plans || []).find(x => x.bundle_id === b.bundle_id)?.positions || [];
        ids = Object.fromEntries(pos.map((p, i) => [p.key, saved[i]?.pos_id || p.key]));
      }
      for (const r of planOnly ? [] : rows) {
        await post("/bordir-tasks", { order_number: d.order_number, product_id: d.product_id, bundle_id: b.bundle_id, operator_id: r.op,
          positions: r.pos.map(k => ({ pos_id: ids[k] })), pcs: Number(r.pcs) });
        made += 1;
      }
      notify.success(planOnly ? { title: "Dicatat: tanpa titik bordir" } : { title: "Pekerjaan dibagikan", message: `${made} tugas dibuat.` });
      ok = true;
    } catch (e) {
      notify.error({ title: made ? `Sebagian tersimpan (${made} dari ${rows.length} tugas)` : "Gagal membagi pekerjaan", message: errMsg(e) });
    }
    reset(b);
    await reload();
    lock.current = false; setBusy(false);
    if (ok && onDone) onDone(brief);
  };
  return (
    <div className="bw" data-testid={`bw-${tid}`}>
      {list.length > 1 && <div className="bw-chips" data-testid={`bw-bundles-${d.product_id}`}>{list.map(x => (
        <button key={x.bundle_id} type="button" className={`bw-chip ${x.bundle_id === b.bundle_id ? "on" : ""}`} onClick={() => reset(x)}
          data-testid={`bw-bundle-${d.product_id}-${x.bundle_id}`}>{x.label} · {x.pcs} pcs</button>))}</div>}
      <div className="bw-progress" data-testid={`bw-step-${tid}-${step}`}>
        <span>Langkah {step} dari 4 · <b>{STEPS[step - 1]}</b></span>
        <span className="bw-dots" aria-hidden="true">{STEPS.map((t, i) => <i key={t} className={step === i + 1 ? "on" : step > i + 1 ? "done" : ""}/>)}</span>
      </div>
      <p className="bw-muted">{b.label} · <b>{b.pcs} pcs</b></p>
      {step === 1 && <PosPicker d={d} b={b} sel={sel} setSel={setSel} custom={custom} setCustom={setCustom} note={note} setNote={setNote} pos={pos} tid={tid}/>}
      {step === 2 && b.plan && <p className="bw-muted" data-testid={`bw-plan-fixed-${tid}`}>Bagian sudah ditetapkan: {pos.map(p => `${p.label} ${p.points} titik`).join(" · ")}</p>}
      {step === 2 && <WhoRows d={d} b={b} pos={pos} rows={rows} setRows={setRows} tid={tid}/>}
      {step === 3 && <WhoRows d={d} b={b} pos={pos} rows={rows} setRows={setRows} tid={tid} part="pcs"/>}
      {step === 4 && <Summary d={d} b={b} pos={pos} rows={rows} tid={tid}/>}
      {tried && <Why errors={step === 1 ? e1 : step === 2 ? eWho : e2} testid={`bw-why-${tid}`}/>}
      <div className="bw-nav">
        {step > (b.plan ? 2 : 1) && <button type="button" className="bw-ghost" onClick={() => setStep(step - 1)} disabled={busy} data-testid={`bw-back-${tid}`}><ChevronLeft size={18}/> Kembali</button>}
        {step === 1 && <button type="button" className="bw-main" onClick={() => (!pos.length && b.spk_points === 0 ? submit(true) : next(e1, 2))} disabled={busy}
          data-testid={`bw-next1-${tid}`}>{!pos.length && b.spk_points === 0 ? "Konfirmasi: tanpa bordir (SPK 0 titik)" : <>Lanjut <ChevronRight size={18}/></>}</button>}
        {step === 2 && <button type="button" className="bw-main" onClick={() => next(eWho, 3)} data-testid={`bw-next2-${tid}`}>Lanjut <ChevronRight size={18}/></button>}
        {step === 3 && <button type="button" className="bw-main" onClick={() => next(e2, 4)} data-testid={`bw-next3-${tid}`}>Lanjut <ChevronRight size={18}/></button>}
        {step === 4 && <button type="button" className="bw-main" onClick={() => submit()} disabled={busy} data-testid={`bw-confirm-${tid}`}>
          {busy ? "Menyimpan…" : "Konfirmasi & Bagikan Pekerjaan"}</button>}
      </div>
    </div>
  );
}
